/**
 * The Specification stage's Detailed Description framework as one query, and its writes.
 *
 * Every write answers the framework whole with what is derived from it, so the cache takes
 * the answer as it is. Writes go one at a time: each edit is made against the framework the
 * last write answered, under its version, so two quick edits never race each other. A
 * refusal because another window saved first reloads the framework and says so; any other
 * refusal is given in the server's words.
 */

import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  DetailedDescriptionFramework,
  FrameworkResponse,
  SectionEdit,
} from "@patent-studio/api-types";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import type { AnalyticsClient } from "@/lib/analytics/analytics-client";
import { ApiError, refusalDetail } from "@/services/api-client";
import { WRITE_TIMEOUT_MS } from "@/services/write-deadline";
import {
  confirmFramework,
  getFramework,
  proposeFramework,
  replaceFramework,
} from "@/services/specification";
import {
  MUTATION_KEYS,
  ReportedFailure,
} from "@/lib/analytics/mutation-reporting";
import { toastNotSaved } from "@/components/ui/ToastSurface";
import { ddFrameworkKey } from "./query-keys";
import { takeServed } from "./take-served";

export const CHANGED_ELSEWHERE =
  "This framework changed in another window. It has been reloaded.";
const NOT_SAVED = "The framework could not be saved. Try again.";

export function useDdFramework(taskId: string) {
  return useQuery({
    queryKey: ddFrameworkKey(taskId),
    queryFn: () => getFramework(taskId),
  });
}

/** The code the server gives a write built on a version another write has replaced. */
const VERSION_CONFLICT = "FRAMEWORK_VERSION_CONFLICT";

/** An edit the page refuses itself, because it cannot be made against what the server holds. */
class EditRefused extends Error {}

/**
 * A write given up on before it was sent, because the queue held it past the deadline. Its
 * stall is reported when it is given up on, so reaching the front later reports nothing more.
 */
class Stalled extends ReportedFailure {}

/** The edit as the page sends it, and whether its request has started or it was given up on. */
interface Asked {
  edit: FrameworkEdit;
  /** What the attorney is told when the edit cannot be made. */
  refused?: string;
  /** Which control made the edit, for the report of a write the queue held. */
  control?: string;
}

/** What the caller hears of one write: that it landed, or that it did not. */
interface Sent {
  onSuccess?: (served: FrameworkResponse) => void;
  onError?: (error: Error) => void;
}

interface Ticket {
  started: boolean;
  abandoned: boolean;
}

/** The writes of each client's task queues whose request is out, each under its own deadline. */
const inFlight = new WeakMap<QueryClient, Map<string, Set<Ticket>>>();

function flying(qc: QueryClient, taskId: string): Set<Ticket> {
  const queues = inFlight.get(qc) ?? new Map<string, Set<Ticket>>();
  inFlight.set(qc, queues);
  const held = queues.get(taskId) ?? new Set<Ticket>();
  queues.set(taskId, held);
  return held;
}

/**
 * An edit as a function of the framework the server holds when the edit is sent, so it is
 * made against the last write's answer. Null when it cannot be made there.
 */
export type FrameworkEdit = (
  served: DetailedDescriptionFramework,
) => SectionEdit[] | null;

/** One queue per task: a write waits for the one before it to land. */
function writes(taskId: string) {
  return { id: `dd-framework:${taskId}` };
}

function holding(qc: QueryClient, taskId: string): FrameworkResponse {
  const served = qc.getQueryData<FrameworkResponse>(ddFrameworkKey(taskId));
  if (served === undefined) throw new EditRefused(NOT_SAVED);
  return served;
}

/**
 * Tell the attorney a write did not land. A refusal the page made itself cannot
 * go again unchanged; any other can, because each write replaces the framework
 * whole under the version it read, so a second send of one that did land after
 * all is refused as stale and reloads rather than applying twice.
 */
function refused(
  qc: QueryClient,
  taskId: string,
  error: Error,
  retry: () => void,
): void {
  if (error instanceof EditRefused) {
    toast.error(error.message);
    return;
  }
  if (error instanceof ApiError && error.code === VERSION_CONFLICT) {
    toast.info(CHANGED_ELSEWHERE);
    void qc.invalidateQueries({ queryKey: ddFrameworkKey(taskId) });
    return;
  }
  toastNotSaved(refusalDetail(error, NOT_SAVED), retry);
}

/**
 * Tell analytics a write was given up on in the queue: how many writes wait, how long the oldest
 * has, and which control made the edit, so the next run says what held the queue.
 */
function reportStalled(
  qc: QueryClient,
  analytics: AnalyticsClient,
  taskId: string,
  asked: Asked,
): void {
  const held = qc.getMutationCache().findAll({
    predicate: (m) =>
      m.options.scope?.id === writes(taskId).id && m.state.status === "pending",
  });
  const oldest = Math.min(...held.map((m) => m.state.submittedAt));
  analytics.capture("write_stalled", {
    where: MUTATION_KEYS.frameworkReplace[0],
    control: asked.control ?? "edit",
    waiting: held.length,
    oldest_ms: Number.isFinite(oldest) ? Date.now() - oldest : null,
  });
}

/**
 * Replace the framework by an edit made against what the server last answered.
 *
 * A write waits in the task's queue for the one before it, and its own deadline starts only once
 * its request is sent. One held in the queue past that deadline is given up on and told as not
 * saved with Retry, its words kept, and the queue's state is reported, so no edit is lost silently
 * whatever holds the queue (gutter-2, 14/10/2026).
 */
export function useReplaceFramework(taskId: string) {
  const qc = useQueryClient();
  const analytics = useAnalytics();
  const mutation = useMutation({
    mutationKey: MUTATION_KEYS.frameworkReplace,
    scope: writes(taskId),
    mutationFn: ({
      edit,
      refused: refusal = NOT_SAVED,
      ticket,
    }: Asked & { ticket: Ticket }) => {
      ticket.started = true;
      if (ticket.abandoned) throw new Stalled(NOT_SAVED);
      const { framework } = holding(qc, taskId);
      const sections = edit(framework);
      if (sections === null) throw new EditRefused(refusal);
      // Counted in flight while its request is out, here and not in a caller's callback, which a
      // later call on the same hook replaces.
      const out = flying(qc, taskId);
      out.add(ticket);
      return replaceFramework(taskId, {
        sections,
        version: framework.version,
      }).finally(() => out.delete(ticket));
    },
    onSuccess: (served) => takeServed(qc, ddFrameworkKey(taskId), served),
    onError: (error, asked) => {
      // A write given up on was told so when it was; reaching the front later, it sends nothing.
      if (error instanceof Stalled) return;
      refused(qc, taskId, error, () => {
        send(asked);
      });
    },
  });
  const send = (asked: Asked, options: Sent = {}) => {
    const ticket: Ticket = { started: false, abandoned: false };
    let timer: ReturnType<typeof setTimeout>;
    const expire = () => {
      if (ticket.started) return;
      // A write ahead whose request is out ends under its own deadline: wait on.
      if (flying(qc, taskId).size > 0) {
        timer = setTimeout(expire, WRITE_TIMEOUT_MS);
        return;
      }
      ticket.abandoned = true;
      reportStalled(qc, analytics, taskId, asked);
      toastNotSaved(NOT_SAVED, () => {
        send(asked, options);
      });
      options.onError?.(new Stalled(NOT_SAVED));
    };
    timer = setTimeout(expire, WRITE_TIMEOUT_MS);
    // The promise of this one call settles for it, whatever later calls on the hook do.
    mutation.mutateAsync({ ...asked, ticket }).then(
      (served) => {
        clearTimeout(timer);
        options.onSuccess?.(served);
      },
      (error: unknown) => {
        clearTimeout(timer);
        if (error instanceof Error && !(error instanceof Stalled))
          options.onError?.(error);
      },
    );
  };
  const sendAsync = (asked: Asked) =>
    new Promise<FrameworkResponse>((resolve, reject) => {
      send(asked, { onSuccess: resolve, onError: reject });
    });
  return { ...mutation, mutate: send, mutateAsync: sendAsync };
}

/** Confirm the framework as the last write left it, by the address it was served. */
export function useConfirmFramework(taskId: string) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationKey: MUTATION_KEYS.frameworkConfirm,
    scope: writes(taskId),
    mutationFn: () => confirmFramework(taskId, holding(qc, taskId).address),
    onSuccess: (served) => takeServed(qc, ddFrameworkKey(taskId), served),
    onError: (error) => {
      refused(qc, taskId, error, () => {
        mutation.mutate();
      });
    },
  });
  return mutation;
}

/** Ask Praxis for a framework; the run lands through the worker, and the stream says when. */
export function useProposeFramework(
  taskId: string,
  failed = "The framework could not be proposed.",
) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.frameworkPropose,
    mutationFn: () => proposeFramework(taskId),
    onSuccess: (served) => takeServed(qc, ddFrameworkKey(taskId), served),
    onError: (error) => {
      toast.error(refusalDetail(error, failed));
    },
  });
}
